export interface WatermarkOptions {
  /** Center text, typically the business website. Defaults to 'www.AgenticPNW.com'. */
  centerText?: string;
  /** Left side label. Defaults to 'CabinetVision'. */
  leftLabel?: string;
  /** Right side label. Defaults to 'AgenticPNW'. */
  rightLabel?: string;
  /** Business phone, shown on the contact row. Blank values are skipped. */
  phone?: string;
  /** Business email, shown on the contact row. Blank values are skipped. */
  email?: string;
}

const DEFAULT_OPTIONS: Required<WatermarkOptions> = {
  centerText: 'www.AgenticPNW.com',
  leftLabel: 'CabinetVision',
  rightLabel: 'AgenticPNW',
  phone: '503-319-5182',
  email: '',
};

/**
 * Add a professional watermark to an image before downloading.
 * Fetches the image as a blob (bypasses CORS for canvas), draws it,
 * overlays the watermark bar, and returns a JPEG data URL.
 */
export async function addWatermark(
  imageUrl: string,
  options?: WatermarkOptions
): Promise<string> {
  const opts: Required<WatermarkOptions> = { ...DEFAULT_OPTIONS, ...options };

  // Strategy 1: Fetch as blob → blob URL is same-origin → canvas works freely
  try {
    const response = await fetch(imageUrl, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      const result = await drawWatermark(blobUrl, opts);
      return result;
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch {
    // Strategy 2: Try crossOrigin='anonymous' (works if server sends CORS headers)
    try {
      return await drawWatermarkWithCrossOrigin(imageUrl, opts);
    } catch {
      // Strategy 3: Can't watermark — return original
      throw new Error('Unable to watermark image');
    }
  }
}

function drawWatermark(imageSrc: string, opts: Required<WatermarkOptions>): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const dataUrl = renderWatermarkCanvas(img, opts);
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageSrc;
  });
}

function drawWatermarkWithCrossOrigin(imageSrc: string, opts: Required<WatermarkOptions>): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const dataUrl = renderWatermarkCanvas(img, opts);
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image with CORS'));
    img.src = imageSrc;
  });
}

function renderWatermarkCanvas(img: HTMLImageElement, opts: Required<WatermarkOptions>): string {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');

  ctx.drawImage(img, 0, 0);

  const w = canvas.width;
  const h = canvas.height;

  // Build the contact line from whatever is provided (phone / email).
  const contactText = [opts.phone, opts.email]
    .map((part) => part.trim())
    .filter(Boolean)
    .join('   •   ');
  const hasContactRow = contactText.length > 0;

  // Single-row bar: ~6.5% of height. A second contact row roughly doubles it.
  const rowHeight = Math.max(28, Math.min(64, Math.round(h * 0.065)));
  const barHeight = hasContactRow ? rowHeight * 2 : rowHeight;
  const barTop = h - barHeight;

  // Semi-transparent white bar at bottom
  ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
  ctx.fillRect(0, barTop, w, barHeight);

  // Blue accent line at top of bar
  ctx.fillStyle = 'rgba(0, 102, 204, 0.25)';
  ctx.fillRect(0, barTop, w, 2);

  // Vertical center of the top (brand) row.
  const topRowCenterY = barTop + rowHeight / 2;

  // Main center text (website / business name)
  const fontSize = Math.max(14, Math.min(24, Math.round(rowHeight * 0.48)));
  ctx.font = `700 ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.shadowColor = 'rgba(255, 255, 255, 0.9)';
  ctx.shadowBlur = 6;
  ctx.fillStyle = '#002952';
  ctx.fillText(opts.centerText, w / 2, topRowCenterY);

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;

  // Small side labels
  const smallFontSize = Math.max(10, Math.min(16, Math.round(rowHeight * 0.34)));
  ctx.font = `600 ${smallFontSize}px Inter, system-ui, -apple-system, sans-serif`;

  ctx.textAlign = 'left';
  ctx.fillStyle = '#0066cc';
  ctx.fillText(opts.leftLabel, 20, topRowCenterY);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#0066cc';
  ctx.fillText(opts.rightLabel, w - 20, topRowCenterY);

  // Contact row (phone • email), centered in the lower half of the bar.
  if (hasContactRow) {
    const contactCenterY = barTop + rowHeight + rowHeight / 2;
    const contactFontSize = Math.max(11, Math.min(18, Math.round(rowHeight * 0.4)));
    ctx.font = `600 ${contactFontSize}px Inter, system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillStyle = '#334155';
    ctx.fillText(contactText, w / 2, contactCenterY);
  }

  return canvas.toDataURL('image/jpeg', 0.92);
}
